import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper, Grid, Card, CardContent, Typography, Button, Chip, Tabs, Tab, Box, CircularProgress,
  Select, MenuItem, Link, makeStyles,
} from '@material-ui/core';
import { kycService } from '../services';
import { fmtDateTimeIST } from '../components/analytics/format';

const useStyles = makeStyles(() => ({
  header: { fontWeight: 600, marginBottom: 16 },
  controls: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  tabs: { borderBottom: '1px solid #e5e7eb' },
  card: { height: '100%', display: 'flex', flexDirection: 'column' },
  name: { fontWeight: 700, fontSize: 15 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 2 },
  docs: { display: 'flex', gap: 8, marginTop: 12 },
  docBox: { flex: 1, textAlign: 'center' },
  docImg: { width: '100%', height: 120, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f3f4f6', cursor: 'pointer' },
  docLabel: { fontSize: 11, color: '#9ca3af', marginTop: 2 },
  noDoc: { width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px dashed #e5e7eb', color: '#9ca3af', fontSize: 12 },
  actions: { display: 'flex', gap: 8, marginTop: 14 },
  reason: { color: '#b91c1c', fontSize: 12, marginTop: 8 },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 24 },
}));

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' },
];

const STATUS_COLOR: Record<string, any> = {
  pending: { background: '#fff7ed', color: '#c2410c' },
  approved: { background: '#ecfdf5', color: '#047857' },
  rejected: { background: '#fef2f2', color: '#b91c1c' },
};

type Submission = {
  _id: string;
  userId: string;
  userKind: 'parent' | 'tutor';
  idType: string;
  status: string;
  rejectionReason?: string | null;
  submittedOn: string;
  frontUrl?: string | null;
  backUrl?: string | null;
  user?: { name?: string; mobile?: string; userType?: string } | null;
};

const VerificationsScreen: React.FC = () => {
  const classes = useStyles();
  const [status, setStatus] = useState('pending');
  const [userKind, setUserKind] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await kycService.list(status, userKind, page, 24);
    if (res.ok) {
      setRows(res.data?.submissions || []);
      setTotal(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    }
    setLoading(false);
  }, [status, userKind, page]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (s: Submission) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Approve KYC for ${s.user?.name || 'this user'}? They'll be notified.`)) return;
    setBusyId(s._id);
    const res = await kycService.approve(s.userId);
    setBusyId('');
    if (!res.ok) { window.alert(res.error || 'Failed to approve'); return; }
    load();
  };

  const handleReject = async (s: Submission) => {
    // eslint-disable-next-line no-restricted-globals
    const reason = window.prompt(`Reject KYC for ${s.user?.name || 'this user'}? Enter a reason (sent to the user):`, '');
    if (reason === null) return;
    setBusyId(s._id);
    const res = await kycService.reject(s.userId, reason);
    setBusyId('');
    if (!res.ok) { window.alert(res.error || 'Failed to reject'); return; }
    load();
  };

  const doc = (url: string | null | undefined, label: string) => (
    <div className={classes.docBox}>
      {url
        ? <img className={classes.docImg} src={url} alt={label} onClick={() => window.open(url, '_blank')} />
        : <div className={classes.noDoc}>No {label}</div>}
      <div className={classes.docLabel}>{label}</div>
    </div>
  );

  return (
    <div>
      <Typography variant="h5" className={classes.header}>Verifications</Typography>

      <Paper elevation={1} style={{ padding: 16 }}>
        <div className={classes.controls}>
          <Tabs
            className={classes.tabs}
            value={status}
            onChange={(_, v) => { setPage(1); setStatus(v); }}
            indicatorColor="primary"
            textColor="primary"
          >
            {STATUS_TABS.map((t) => <Tab key={t.value || 'all'} value={t.value} label={t.label} />)}
          </Tabs>
          <Select
            variant="outlined"
            margin="dense"
            value={userKind}
            onChange={(e) => { setPage(1); setUserKind(e.target.value as string); }}
            style={{ minWidth: 150 }}
          >
            <MenuItem value="">All users</MenuItem>
            <MenuItem value="tutor">Teachers</MenuItem>
            <MenuItem value="parent">Parents</MenuItem>
          </Select>
        </div>

        {loading ? (
          <Box p={6} display="flex" justifyContent="center"><CircularProgress /></Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {rows.map((s) => {
                const pending = s.status === 'pending';
                return (
                  <Grid item xs={12} sm={6} md={4} key={s._id}>
                    <Card className={classes.card} elevation={1}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography className={classes.name}>{s.user?.name || 'Unknown'}</Typography>
                          <Chip size="small" label={s.status} style={STATUS_COLOR[s.status] || {}} />
                        </Box>
                        <Typography className={classes.meta}>
                          {s.userKind === 'tutor' ? 'Teacher' : 'Parent'} · {s.idType} · {s.user?.mobile || '—'}
                        </Typography>
                        <Typography className={classes.meta}>Submitted {fmtDateTimeIST(s.submittedOn)}</Typography>

                        <div className={classes.docs}>
                          {doc(s.frontUrl, 'front')}
                          {doc(s.backUrl, 'back')}
                        </div>

                        {s.status === 'rejected' && s.rejectionReason ? (
                          <div className={classes.reason}>Reason: {s.rejectionReason}</div>
                        ) : null}

                        {pending && (
                          <div className={classes.actions}>
                            <Button size="small" variant="contained" color="primary" disabled={busyId === s._id} onClick={() => handleApprove(s)}>Approve</Button>
                            <Button size="small" variant="outlined" disabled={busyId === s._id} onClick={() => handleReject(s)}>Reject</Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {rows.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">No submissions.</Typography>
                </Grid>
              )}
            </Grid>

            <div className={classes.pager}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Typography variant="body2">Page {page} / {totalPages} · {total} total</Typography>
              <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </>
        )}
      </Paper>
    </div>
  );
};

export default VerificationsScreen;
